import {
  createContext,
  useContext,
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { Category, Publication } from '@publiqa/shared'

export interface NavPage {
  id: string
  slug: string
  title: string
  isHome: boolean
  showInNav: boolean
}

export interface PublicCtx {
  publication: Publication
  pages: NavPage[]
  categories: Category[]
  /** Préfixe des liens internes : '' sur un domaine propre, '/p/<slug>' sinon. */
  base: string
  /** Aperçu (brouillons visibles) — propagé dans les liens et appels d'API. */
  preview: boolean
  /** Rendu dans l'éditeur : les liens ne naviguent pas, les formulaires n'envoient rien. */
  inert: boolean
  /** Ancres des blocs masqués : les liens qui y mènent disparaissent des menus. */
  hiddenAnchors?: string[]
  /**
   * Bulletins d'inscription fermés (aucune saison ouverte) : ancre → message
   * de fermeture. Leurs ancres figurent aussi dans `hiddenAnchors`.
   */
  closedAnchors?: Record<string, string>
  /** Sections dont un bouton mène à un bulletin fermé (ex. « Inscriptions »). */
  closedVia?: string[]
}

const Ctx = createContext<PublicCtx | null>(null)

export function PublicProvider({ value, children }: { value: PublicCtx; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function usePublic(): PublicCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('usePublic hors d’une publication')
  return ctx
}

export function useMaybePublic(): PublicCtx | null {
  return useContext(Ctx)
}

/**
 * État d'un lien vis-à-vis des blocs masqués : `hidden` s'il mène à un bloc
 * absent du site ; `closedMessage` s'il s'agit d'un bulletin fermé ;
 * `leadsToClosed` s'il mène à une section qui renvoie elle-même au bulletin
 * fermé (« S'inscrire » → section « Inscriptions » → bulletin).
 */
export function useLinkState(): (href: string) => {
  hidden: boolean
  closedMessage?: string
  leadsToClosed?: boolean
} {
  const ctx = useMaybePublic()
  return (href) => {
    const anchor = /^\/?#([\w-]+)$/.exec(href)?.[1]
    if (!anchor) return { hidden: false }
    if (!ctx?.hiddenAnchors?.includes(anchor))
      return { hidden: false, leadsToClosed: !!ctx?.closedVia?.includes(anchor) }
    return { hidden: true, closedMessage: ctx.closedAnchors?.[anchor] }
  }
}

/** Retire des menus les liens vers un bloc masqué (« /#tarifs » si « tarifs » l'est). */
export function useVisibleLinks<T extends { href: string }>(links: T[]): T[] {
  const ctx = useMaybePublic()
  const hidden = ctx?.hiddenAnchors ?? []
  if (!hidden.length) return links
  return links.filter((l) => {
    const anchor = /^\/?#([\w-]+)$/.exec(l.href)?.[1]
    return !anchor || !hidden.includes(anchor)
  })
}

/** Chemin de l'API publique pour la publication courante. */
export function usePubliqApi(): (
  path: string,
  params?: Record<string, string | number | undefined>,
) => string {
  const { publication, preview } = usePublic()
  return (path, params = {}) => {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== '') search.set(k, String(v))
    if (preview) search.set('preview', '1')
    const qs = search.toString()
    return `/public/p/${publication.slug}${path}${qs ? `?${qs}` : ''}`
  }
}

/** Transforme un lien saisi dans le back-office (« /contact ») en lien réel. */
export function usePublicHref(): (href: string) => string {
  const ctx = useMaybePublic()
  return (href) => {
    if (!ctx || !href.startsWith('/')) return href
    const [path = '', hash] = href.split('#')
    const full = `${ctx.base}${path === '/' ? '' : path}` || '/'
    const withPreview = ctx.preview ? `${full}?preview=1` : full
    return hash ? `${withPreview}#${hash}` : withPreview
  }
}

/**
 * Fait défiler jusqu'au bloc d'ancre `id`. Le contenu d'une page arrive de
 * l'API APRÈS la navigation : si le bloc n'existe pas encore, on réessaie
 * pendant 3 s plutôt que d'abandonner en silence (cas d'un clic sur
 * « Tarifs » depuis une page article).
 */
export function scrollToAnchor(id: string, { initial = false }: { initial?: boolean } = {}): void {
  const started = Date.now()
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const find = () => document.getElementById(decodeURIComponent(id))
  const attempt = () => {
    const el = find()
    if (!el) {
      if (Date.now() - started < 8000) window.setTimeout(attempt, 100)
      return
    }
    // Premier chargement (lien partagé « …#tarifs ») : saut immédiat, puis
    // recalages — les images et blocs chargés après coup décalent la page,
    // et un défilement doux en cours serait perdu.
    el.scrollIntoView({ behavior: initial || reduce ? 'auto' : 'smooth', block: 'start' })
    if (initial) {
      for (const delay of [400, 1200]) {
        window.setTimeout(() => find()?.scrollIntoView({ behavior: 'auto', block: 'start' }), delay)
      }
    }
  }
  attempt()
}

/**
 * Ancre actuellement à l'écran parmi `ids` (surlignage du menu au défilement).
 * Les blocs arrivent après coup : l'observation est (re)branchée tant qu'ils
 * ne sont pas tous trouvés.
 */
export function useActiveAnchor(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null)
  const { pathname } = useLocation()
  const key = ids.join(',')
  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === 'undefined') return
    const visible = new Map<string, number>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries)
          visible.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0)
        // Le premier bloc (dans l'ordre du menu) qui coupe la bande médiane.
        setActive(ids.find((id) => (visible.get(id) ?? 0) > 0) ?? null)
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.01] },
    )
    const observed = new Set<string>()
    let tries = 0
    const attach = () => {
      for (const id of ids) {
        const el = document.getElementById(id)
        if (el && !observed.has(id)) {
          observer.observe(el)
          observed.add(id)
        }
      }
      if (observed.size < ids.length && tries++ < 30) timer = window.setTimeout(attach, 200)
    }
    let timer = window.setTimeout(attach, 0)
    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
      setActive(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, pathname])
  return active
}

/** Lien interne (navigation sans rechargement) ou externe, selon `href`. */
export function SmartLink({
  href,
  children,
  onClick,
  ...rest
}: { href: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const ctx = useMaybePublic()
  const resolve = usePublicHref()
  const location = useLocation()
  const navigate = useNavigate()
  if (ctx?.inert) {
    return (
      <a href="#" onClick={(e) => e.preventDefault()} {...rest}>
        {children}
      </a>
    )
  }
  // Ancre seule (« #tarifs ») : même page, on défile.
  if (href.startsWith('#') && href.length > 1) {
    return (
      <a
        href={href}
        onClick={(e) => {
          onClick?.(e)
          e.preventDefault()
          scrollToAnchor(href.slice(1))
          history.replaceState(history.state, '', href)
        }}
        {...rest}
      >
        {children}
      </a>
    )
  }
  if (href.startsWith('/')) {
    const to = resolve(href)
    const [target = '', hash] = to.split('#')
    return (
      <Link
        to={to}
        onClick={(e) => {
          onClick?.(e)
          // Ancre de la page DÉJÀ affichée : pas de navigation (rien ne
          // changerait au second clic), on défile directement.
          if (target.split('?')[0] !== location.pathname) return
          if (hash) {
            e.preventDefault()
            scrollToAnchor(hash)
            void navigate(to, { replace: true, preventScrollReset: true })
          } else if (!location.hash) {
            // Lien vers la page courante (logo sur l'accueil) : retour en haut.
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }}
        {...rest}
      >
        {children}
      </Link>
    )
  }
  const external = /^https?:/.test(href)
  return (
    <a
      href={href || '#'}
      onClick={onClick}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {children}
    </a>
  )
}
